import type { CoachPermissionFlag, PermissionKey } from '@/stores/authStore';
import type { ProfileType, UserProfile, UserRole } from '@ergshoot/shared';
import {
  Activity,
  BookOpen,
  Building2,
  ClipboardCheck,
  CreditCard,
  DollarSign,
  Dumbbell,
  FileText,
  Globe,
  History,
  Home,
  Layers,
  LayoutDashboard,
  type LucideIcon,
  Medal,
  Megaphone,
  Settings,
  Shield,
  Target,
  TrendingUp,
  Trophy,
  User,
  UserCog,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';

export type NavigationRole = Exclude<UserRole, 'purchaser'>;
export type NavigationSurface = 'desktop' | 'mobile';

export interface NavigationItem {
  id: string;
  label: string;
  href?: string;
  icon: LucideIcon;
  badge?: string;
  permission?: PermissionKey;
  coachFlag?: CoachPermissionFlag;
  roles?: NavigationRole[];
  requiresProfile?: ProfileType[];
  showUpgradeModalIfNoProfile?: ProfileType[];
  children?: NavigationItem[];
  /**
   * Preserves an existing renderer-specific visibility rule while the shared
   * schema removes all duplicated route and permission declarations.
   */
  rolesBySurface?: Partial<Record<NavigationSurface, NavigationRole[]>>;
}

export type NavigationSectionId =
  | 'management'
  | 'agent'
  | 'fitness'
  | 'compete'
  | 'content'
  | 'personal';

export interface NavigationSection {
  id: NavigationSectionId;
  label: string;
  items: NavigationItem[];
}

export interface NavigationVisibilityContext {
  roles: NavigationRole[];
  profiles: UserProfile[];
  sessionActiveRoles: UserRole[] | null;
  surface: NavigationSurface;
  hasPermission: (permission: PermissionKey) => boolean;
  hasCoachFlag: (flag: CoachPermissionFlag) => boolean;
}

export const COLLAPSIBLE_NAVIGATION_SECTIONS: NavigationSectionId[] = [
  'management',
  'agent',
  'fitness',
  'compete',
  'content',
  'personal',
];

export const NAVIGATION_SECTIONS: NavigationSection[] = [
  {
    id: 'management',
    label: 'MANAGEMENT',
    items: [
      {
        id: 'admin-dashboard',
        label: 'Dashboard',
        href: '/admin',
        icon: LayoutDashboard,
        roles: ['hq_admin'],
        permission: 'view_system_metrics',
      },
      {
        id: 'admin-analytics',
        label: 'Analytics',
        href: '/admin/analytics',
        icon: Activity,
        roles: ['hq_admin'],
        permission: 'view_system_metrics',
      },
      {
        id: 'admin-users-group',
        label: 'Users & Roles',
        icon: Users,
        roles: ['hq_admin'],
        permission: 'manage_users',
        children: [
          {
            id: 'admin-users',
            label: 'All Users',
            href: '/admin/users',
            icon: Users,
            roles: ['hq_admin'],
            permission: 'manage_users',
          },
          {
            id: 'admin-permissions',
            label: 'Permissions',
            href: '/admin/permissions',
            icon: Shield,
            roles: ['hq_admin'],
            permission: 'view_system_metrics',
          },
          {
            id: 'admin-team',
            label: 'HQ Team',
            href: '/admin/team',
            icon: Shield,
            roles: ['hq_admin'],
            permission: 'manage_users',
          },
          {
            id: 'admin-audit-logs',
            label: 'Audit Logs',
            href: '/admin/audit-logs',
            icon: History,
            roles: ['hq_admin'],
            permission: 'view_system_metrics',
          },
        ],
      },
      {
        id: 'admin-affiliates',
        label: 'Affiliates',
        href: '/admin/affiliates',
        icon: Building2,
        roles: ['hq_admin'],
        permission: 'approve_affiliates',
      },
      {
        id: 'admin-agents-group',
        label: 'Agents',
        icon: UserCog,
        roles: ['hq_admin'],
        permission: 'view_system_metrics',
        children: [
          {
            id: 'admin-agents',
            label: 'All Agents',
            href: '/admin/agents',
            icon: UserCog,
            roles: ['hq_admin'],
            permission: 'view_system_metrics',
          },
          {
            id: 'admin-agent-leads',
            label: 'Agent Enquiries',
            href: '/admin/agent-leads',
            icon: UserPlus,
            roles: ['hq_admin'],
            permission: 'approve_affiliates',
          },
          {
            id: 'admin-territories',
            label: 'Territories',
            href: '/admin/territories',
            icon: Globe,
            roles: ['hq_admin'],
            permission: 'view_system_metrics',
          },
        ],
      },
      {
        id: 'admin-money-group',
        label: 'Money',
        icon: DollarSign,
        roles: ['hq_admin'],
        permission: 'view_commissions',
        children: [
          {
            id: 'admin-stripe-console',
            label: 'Stripe Console',
            href: '/admin/stripe-console',
            icon: CreditCard,
            roles: ['hq_admin'],
            permission: 'view_commissions',
          },
          {
            id: 'admin-commissions',
            label: 'Commissions',
            href: '/admin/commissions',
            icon: DollarSign,
            roles: ['hq_admin'],
            permission: 'view_commissions',
          },
          {
            id: 'admin-subscriptions',
            label: 'Subscriptions',
            href: '/admin/subscriptions',
            icon: Wallet,
            roles: ['hq_admin'],
            permission: 'view_commissions',
          },
        ],
      },
    ],
  },
  {
    id: 'agent',
    label: 'AGENT TOOLS',
    items: [
      {
        id: 'agent-dashboard',
        label: 'Dashboard',
        href: '/agent',
        icon: LayoutDashboard,
        roles: ['agent'],
        permission: 'view_commissions',
      },
      {
        id: 'agent-affiliates',
        label: 'My Affiliates',
        href: '/agent/affiliates',
        icon: Building2,
        roles: ['agent'],
        permission: 'view_commissions',
      },
      {
        id: 'agent-commission',
        label: 'Commissions',
        href: '/agent/commission',
        icon: DollarSign,
        roles: ['agent'],
        permission: 'view_commissions',
      },
      {
        id: 'agent-payouts',
        label: 'Payouts',
        href: '/agent/payouts',
        icon: Wallet,
        roles: ['agent'],
        permission: 'view_commissions',
      },
    ],
  },
  {
    id: 'fitness',
    label: 'PROGRAMME',
    items: [
      {
        id: 'admin-workouts',
        label: 'Daily WODs',
        href: '/admin/programming',
        icon: Dumbbell,
        roles: ['hq_admin'],
        permission: 'upload_programming',
      },
      {
        id: 'admin-standards',
        label: 'Standards',
        href: '/admin/standards',
        icon: Target,
        roles: ['hq_admin'],
        permission: 'upload_programming',
      },
      {
        id: 'admin-tracks',
        label: 'Specialty',
        href: '/admin/tracks',
        icon: Layers,
        roles: ['hq_admin'],
        permission: 'upload_programming',
      },
      {
        id: 'workouts',
        label: 'Daily WODs',
        href: '/workouts',
        icon: Dumbbell,
        roles: ['agent', 'affiliate_owner', 'affiliate_coach', 'member', 'individual'],
      },
      {
        id: 'standards',
        label: 'The Standards',
        href: '/standards',
        icon: Target,
        roles: ['agent', 'affiliate_owner', 'affiliate_coach', 'member', 'individual'],
      },
    ],
  },
  {
    id: 'compete',
    label: 'COMPETE',
    items: [
      {
        id: 'leaderboards',
        label: 'Leaderboards',
        href: '/leaderboard',
        icon: Medal,
        roles: ['hq_admin', 'affiliate_owner', 'affiliate_coach', 'member', 'individual'],
        permission: 'view_leaderboard',
      },
      {
        id: 'events',
        label: 'Events',
        href: '/events',
        icon: Trophy,
        roles: ['hq_admin', 'affiliate_owner', 'affiliate_coach', 'member', 'individual'],
      },
      {
        id: 'my-progress',
        label: 'My Progress',
        href: '/progress',
        icon: TrendingUp,
        roles: ['individual', 'member'],
      },
      {
        id: 'community',
        label: 'Community',
        href: '/community',
        icon: Users,
        roles: ['affiliate_owner', 'affiliate_coach', 'member', 'individual'],
      },
    ],
  },
  {
    id: 'content',
    label: 'CONTENT',
    items: [
      {
        id: 'assets',
        label: 'Assets',
        href: '/resources/assets',
        icon: FileText,
        permission: 'access_assets',
      },
      {
        id: 'manuals',
        label: 'Manuals',
        href: '/resources/manuals',
        icon: BookOpen,
        permission: 'access_assets',
      },
      {
        id: 'news',
        label: 'News',
        href: '/news',
        icon: Megaphone,
      },
    ],
  },
  {
    id: 'personal',
    label: 'MY ACCOUNT',
    items: [
      {
        id: 'my-hub',
        label: 'Dashboard',
        href: '/hub',
        icon: Home,
        showUpgradeModalIfNoProfile: ['affiliate_member', 'individual'],
        rolesBySurface: {
          desktop: ['agent', 'affiliate_owner', 'affiliate_coach', 'member', 'individual'],
        },
      },
      {
        id: 'my-team',
        label: 'My Team',
        href: '/affiliate/members',
        icon: Users,
        roles: ['affiliate_owner', 'affiliate_coach', 'hq_admin'],
        permission: 'manage_users',
        coachFlag: 'can_manage_members',
      },
      {
        id: 'team-management',
        label: 'Team Management',
        href: '/affiliate/team/management',
        icon: Shield,
        roles: ['affiliate_owner'],
      },
      {
        id: 'my-gyms',
        label: 'My Gyms',
        href: '/affiliate/my-gyms',
        icon: Building2,
        roles: ['affiliate_owner'],
      },
      {
        id: 'score-approvals',
        label: 'Score Approvals',
        href: '/affiliate/approvals',
        icon: ClipboardCheck,
        roles: ['affiliate_owner', 'affiliate_coach'],
        coachFlag: 'can_approve_scores',
      },
      {
        id: 'agent-leaderboards',
        label: 'Leaderboards',
        href: '/leaderboard',
        icon: Medal,
        roles: ['agent'],
        permission: 'view_leaderboard',
      },
      {
        id: 'agent-events',
        label: 'Events',
        href: '/events',
        icon: Trophy,
        roles: ['agent'],
      },
      {
        id: 'billing',
        label: 'Billing',
        href: '/billing',
        icon: CreditCard,
        roles: ['affiliate_owner', 'affiliate_coach', 'individual'],
        permission: 'manage_billing',
        coachFlag: 'can_view_billing',
      },
      {
        id: 'affiliate-payouts',
        label: 'Payouts',
        href: '/affiliate/payouts',
        icon: Wallet,
        roles: ['affiliate_owner'],
      },
      {
        id: 'individual-profile',
        label: 'My Profile',
        href: '/individual/profile',
        icon: User,
        roles: ['individual'],
      },
      {
        id: 'account',
        label: 'Settings',
        href: '/settings',
        icon: Settings,
      },
    ],
  },
];

export function deriveNavigationRoles(input: {
  userRole?: UserRole | null;
  profiles: UserProfile[];
  sessionActiveRoles: UserRole[] | null;
}): NavigationRole[] {
  const roles = new Set<NavigationRole>();

  if (input.userRole && input.userRole !== 'purchaser') {
    roles.add(input.userRole);
  }

  for (const profile of input.profiles) {
    if (profile.is_active === false) continue;

    switch (profile.profile_type) {
      case 'hq_admin':
        roles.add('hq_admin');
        break;
      case 'agent':
        roles.add('agent');
        break;
      case 'affiliate_member':
        if (profile.member_role === 'owner') roles.add('affiliate_owner');
        else if (profile.member_role === 'coach') roles.add('affiliate_coach');
        else roles.add('member');
        break;
      case 'individual':
        roles.add('individual');
        break;
    }
  }

  const allRoles = Array.from(roles);
  if (input.sessionActiveRoles === null) {
    return allRoles;
  }

  return allRoles.filter((role) => input.sessionActiveRoles?.includes(role));
}

export function hasRequiredNavigationProfile(
  profiles: UserProfile[],
  requiredProfiles: ProfileType[]
): boolean {
  return profiles.some(
    (profile) => profile.is_active !== false && requiredProfiles.includes(profile.profile_type)
  );
}

export function shouldShowNavigationUpgrade(
  item: NavigationItem,
  profiles: UserProfile[]
): boolean {
  return item.showUpgradeModalIfNoProfile
    ? !hasRequiredNavigationProfile(profiles, item.showUpgradeModalIfNoProfile)
    : false;
}

export function isNavigationItemVisible(
  item: NavigationItem,
  context: NavigationVisibilityContext
): boolean {
  const allowedRoles = item.rolesBySurface?.[context.surface] ?? item.roles;
  if (
    allowedRoles &&
    context.roles.length > 0 &&
    !allowedRoles.some((role) => context.roles.includes(role))
  ) {
    return false;
  }

  const hasPermissionGrant = item.permission ? context.hasPermission(item.permission) : undefined;
  const hasCoachGrant = item.coachFlag
    ? context.roles.includes('affiliate_owner')
      ? !item.permission
      : context.hasCoachFlag(item.coachFlag)
    : undefined;

  if (hasPermissionGrant === false && hasCoachGrant !== true) {
    return false;
  }
  if (hasCoachGrant === false && hasPermissionGrant !== true) {
    return false;
  }

  if (
    item.requiresProfile &&
    !hasRequiredNavigationProfile(context.profiles, item.requiresProfile)
  ) {
    return false;
  }

  return true;
}

export function getVisibleNavigationSections(
  context: NavigationVisibilityContext
): NavigationSection[] {
  return NAVIGATION_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => isNavigationItemVisible(item, context)),
  })).filter((section) => section.items.length > 0);
}

export function getNavigationHomePath(roles: NavigationRole[]): string {
  if (roles.includes('hq_admin')) return '/admin';
  if (roles.includes('agent')) return '/agent';
  if (roles.includes('affiliate_owner') || roles.includes('affiliate_coach')) return '/affiliate';
  return '/hub';
}

export function isNavigationHrefActive(pathname: string, href?: string): boolean {
  return Boolean(href && (pathname === href || pathname.startsWith(`${href}/`)));
}

export function getAutoExpandedNavigation(pathname: string): Record<string, boolean> {
  const expanded: Record<string, boolean> = {};

  for (const section of NAVIGATION_SECTIONS) {
    if (
      section.items.some((item) => isNavigationHrefActive(pathname, item.href)) &&
      COLLAPSIBLE_NAVIGATION_SECTIONS.includes(section.id)
    ) {
      expanded[section.id] = true;
    }

    for (const item of section.items) {
      if (item.children?.some((child) => isNavigationHrefActive(pathname, child.href))) {
        expanded[item.id] = true;
        expanded[section.id] = true;
      }
    }
  }

  return expanded;
}
